/**
 * The practice item's state machine (surfaces.md §1.1), as pure functions.
 *
 * The surface renders a decision and never makes one: every transition here
 * reads what the server said (`ChildVerdict`) and what she did (pressed the
 * door, finished the beats). `truth` exists in the browser only once the
 * server closed the item.
 *
 *     KLAUSIAMA ─Enter─► TIKRINAMA ─┬─ „Taip." ───────────► TAIP (closed)
 *                                   ├─ „Tikrinama…" ─────► NETIKRINTA (closed, unjudged)
 *                                   └─ „Ne visai." ─┬─ closed ──────────────► ATSAKYMAS (truth)
 *                                                   ├─ door taken before ───► RUNG r+1 (auto)
 *                                                   └─ first miss ──────────► DURYS
 *     DURYS ─„Noriu suprasti"─► BEATS ─last beat─► RUNG 1 ─„Bandyti dar kartą"─► retry (KLAUSIAMA)
 */
import type { ChildDoor, ChildExplanation, ChildTruth, ChildVerdict } from "../api";

export type Phase =
  | { at: "KLAUSIAMA" }
  | { at: "TIKRINAMA" }
  | { at: "NETIKRINTA" }
  | { at: "TAIP"; truth: ChildTruth | null }
  | { at: "DURYS"; door: ChildDoor | null }
  | { at: "BEATS"; explanation: ChildExplanation; beat: number; step: number }
  | { at: "RUNG"; rung: number; trigger: "door" | "auto" | "reopen" }
  | { at: "ATSAKYMAS"; truth: ChildTruth | null };

/** What the item remembers across its variants. */
export interface Ladder {
  /** The explanation.opened id once she took the door on this item. */
  opened: string | null;
  /** Rungs shown so far on this item. */
  rung: number;
  /** The ladder's length from the drawing registry (at least 1). */
  of: number;
}

export function afterVerdict(v: ChildVerdict, ladder: Ladder): Phase {
  if (v.word === "Taip.") return { at: "TAIP", truth: v.truth };
  if (v.word === "Tikrinama…" || v.word === null) return { at: "NETIKRINTA" };
  // „Ne visai."
  if (v.closed) return { at: "ATSAKYMAS", truth: v.truth };
  // After the first door, further rungs arrive by themselves (§1.1.1): she
  // asked for help one miss ago, and asking her to press again after she
  // worked the rung would be the machine forgetting what she did.
  if (ladder.opened) return { at: "RUNG", rung: ladder.rung + 1, trigger: "auto" };
  return { at: "DURYS", door: v.door };
}

/** The item has closed: „Toliau" goes to the next item. */
export function isClosed(p: Phase): boolean {
  return p.at === "TAIP" || p.at === "ATSAKYMAS" || p.at === "NETIKRINTA";
}

/** The beat and step after `→`. `null` when the beats are spent (on to RUNG 1). */
export function nextBeat(x: ChildExplanation, beat: number, step: number): { beat: number; step: number } | null {
  const steps = x.beats[beat]?.steps.length ?? 0;
  if (step + 1 < steps) return { beat, step: step + 1 };
  if (beat + 1 < x.beats.length) return { beat: beat + 1, step: 0 };
  return null;
}

/** `←`: the previous step, or the last step of the previous beat. */
export function prevBeat(x: ChildExplanation, beat: number, step: number): { beat: number; step: number } {
  if (step > 0) return { beat, step: step - 1 };
  if (beat > 0) return { beat: beat - 1, step: Math.max(0, (x.beats[beat - 1]?.steps.length ?? 1) - 1) };
  return { beat, step };
}

/** Her answer as one line of text, for the ATSAKYMAS comparison and for a
 *  drawing that draws her answer back. Option ids become their labels. */
export function answerText(input: Record<string, unknown>, labels: Record<string, string> = {}): string {
  const s = (x: unknown) => (typeof x === "string" ? x : "");
  if (Array.isArray(input.picked)) return (input.picked as string[]).map((id) => labels[id] ?? id).join(", ");
  if (Array.isArray(input.seq)) return (input.seq as string[]).map((id) => labels[id] ?? id).join(", ");
  if (Array.isArray(input.parts)) return (input.parts as string[]).join(" : ");
  if (input.slots && typeof input.slots === "object") return Object.values(input.slots as Record<string, string>).join(", ");
  if ("lb" in input) return `${s(input.lb)}${s(input.lo)}; ${s(input.hi)}${s(input.rb)}`;
  if ("m" in input) return `${s(input.m)} · 10^${s(input.e)}`;
  if ("x" in input && "y" in input) return `(${s(input.x)}; ${s(input.y)})`;
  if ("unit" in input) return `${s(input.v)} ${labels[s(input.unit)] ?? s(input.unit_typed)}`.trim();
  if ("n" in input && "d" in input) return `${s(input.w) ? s(input.w) + " " : ""}${s(input.n)}/${s(input.d)}`;
  return s(input.v);
}
